// Лента новостей по серверному адресу (feed_path / more_path): только крупные карточки (подраздел в Figma),
// сверху — закреплённая полупрозрачная панель (Figma Nav Bar 772:30762): «←» слева, название ленты по центру.
// следующая страница по курсору — сама, когда низ ленты подходит к экрану, со спиннером.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNewsFeed } from '../../api/newsQueries';
import { t } from '../../i18n/i18n';
import { AdNewsCard, LeadNewsCard } from '../../ui/NewsCardView';
import { withAds } from '../../ui/newsAds';
import { NavBackButton } from '../../ui/NavBackButton';
import { QueryFallback } from '../../ui/States';
import { Placeholder } from '../Placeholder';
import styles from './News.module.css';

/* Snackbar раздела новостей — боковые поля 8 вместо 24 (хаб 772:30734 / 30737 / 30740, «Все» 772:30770 / 30773 / 30776):
   метка без раскладки, её ловит OfflineBanner.module.css через :has (HQ-2213); общий компонент не правим. */
export const GUTTER_8 = <span hidden data-offline-gutter="8" />;

export function NewsFeedScreen() {
  const state = (useLocation().state ?? {}) as { path?: string; title?: string };
  return (
    <div className={styles.white}>
      {GUTTER_8}
      <header className={styles.navBar}>
        <NavBackButton />
        <h1 className={styles.navTitle}>{state.title ?? t('mc_news')}</h1>
        <span className={styles.navSide} aria-hidden="true" />
      </header>
      <NewsFeedList path={state.path ?? '/api/v5/mobile/screens/cricket/news/'} />
    </div>
  );
}

export function NewsFeedList({ path }: { path: string }) {
  const feed = useNewsFeed(path);
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = feed;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage || isFetchNextPageError) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    }, { rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  if (!feed.data) return <QueryFallback query={feed} />;
  const seen = new Set<number>();
  const seenAds = new Set<string>();
  // реклама — на каждой странице ленты, after_index — внутри своей страницы (гид §2.9)
  const entries = feed.data.pages.flatMap((p) => withAds(
    p.articles.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true))),
    p.ads?.filter((ad) => (seenAds.has(ad.id) ? false : (seenAds.add(ad.id), true)))));
  if (!entries.length) return <Placeholder title={t('mc_news_empty')} text={t('tg_news_soon')} />;
  return (
    <div className={styles.list}>
      {entries.map((e) => e.kind === 'ad'
        ? <AdNewsCard key={e.ad.id} ad={e.ad} place="news_feed" />
        : <LeadNewsCard key={e.article.id} article={e.article} />)}
      {hasNextPage && (
        <div ref={sentinel} className={styles.more}>
          {isFetchNextPageError
            ? <button className={styles.retry} onClick={() => void fetchNextPage()}>{t('mc_retry')}</button>
            : <span className={styles.spinner} role="status" aria-label={t('tg_loading')} />}
        </div>
      )}
    </div>
  );
}
